import React, { useState, useRef } from "react"
import { findDOMNode } from "react-dom"
import FieldMultiselect_PopoverOptionsSelector from "../../../../../generic/users/components/popover/popover-options-selector"
import FieldMultiselect_PopoverSearch from "../../../../../generic/multiselect/components/popover/popover-search"
import VScroll from "../../../../../../gd-components/scrolling/custom-v-scroll/v-scroll"
import Popover from "../../../../../../gd-components/gd-popover/popover-v2"
import PopoverPosition from "../../../../../../gd-components/gd-popover/popover-position"
import { buildDisplayOptions } from "../../../../../generic/users/components/popover/build-options"
import { UsersSelectorGroupBy } from "../../../../../generic/users/@const"
import Task from "../../../../../../model2/task/task"
import { modalAddTaskUser } from "../../../../../../gd-modals/users/add-user"
import { ProjectAccess } from "../../../../../../gd/const/project"
import ProjectUser from "../../../../../../model2/project-user/project-user-backup";

type ProtectedUser = {
    userId  : ID
    hint    : string
}

export type FieldTaskUsers_PopoverMenuProps = {
    parentRef       : React.RefObject<HTMLElement>
    currentOptions  : ID[]
    field           : any
    model           : any
    onOptionClick   : (option: Option, e: React.MouseEvent) => void
    onBulkChange    : (newIds: ID[]) => void
    hidePopover     : () => void
}

export default function FieldTaskUsers_PopoverMenu(props: FieldTaskUsers_PopoverMenuProps) {
    const task = props.model as Task

    const [searchString, setSearchString] = useState<string | null>(null)

    const optionsSelectorRef = useRef<any>(null)
    const scrollRef          = useRef<any>(null)

    const getProtectedUsers = (): ProtectedUser[] => {
        const seen   = new Set<ID>()
        const result : ProtectedUser[] = []
        const add    = (id: ID | null, hint: string) => {
            if (id && !seen.has(id)) {
                seen.add(id)
                result.push({
                    userId  : id,
                    hint    : hint,
                })
            }
            return
        }
        add(task.actionRequiredUserId, "Action required user can't be removed")
        add(task.assignedToUserId,     "Assigned user can't be removed")
        return result
    }

    const onPopoverKeyDown = (e: React.SyntheticEvent) => {
        if (optionsSelectorRef.current) {
            optionsSelectorRef.current.onPopoverKeyDown(e)
        }
        return
    }

    const onAddUser = () => {
        modalAddTaskUser(task)
        props.hidePopover()
    }

    const updateScroll = (node: any) => {
        if (!node || !scrollRef.current) {
            return
        }
        const scrollNode: any = findDOMNode(scrollRef.current.childrenContainer)
        const nodeBottom      = node.offsetTop + node.offsetHeight
        const scrollTop       = scrollNode.scrollTop
        const scrollBottom    = scrollNode.scrollTop + scrollNode.offsetHeight
        if (scrollBottom < nodeBottom) {
            scrollRef.current.setScrollTop(nodeBottom - scrollNode.offsetHeight, false, 100)
            return
        }
        if (scrollTop > node.offsetTop) {
            scrollRef.current.setScrollTop(node.offsetTop, false, 100)
            return
        }
        return
    }

    const isManager       = gd.ac.check.company.isManager()
    const PopoverMenuMore = _.get(props.field.components, 'PopoverMenuMore', false)
    const protectedUsers  = getProtectedUsers()
    const protectedIdSet  = new Set(_.map(protectedUsers, (projectUser: ProjectUser) => {
        return projectUser.userId
    }))
    const projectUserIds  = new Set<ID>(task.defaultProject?.userIds(ProjectAccess.LIMITED) ?? [])

    const disabledMap = new Map<ID, string>(_.map(protectedUsers, (pu): [ID, string] => {
        return [pu.userId, pu.hint]
    }))
    if (props.currentOptions.length === 1) {
        const lastId = props.currentOptions[0]
        if (!disabledMap.has(lastId)) {
            disabledMap.set(lastId, "At least one user must remain")
        }
    }

    const allDisplayOptions      = buildDisplayOptions(props.field, searchString, UsersSelectorGroupBy.NONE, new Set<string>())
    const filteredDisplayOptions = allDisplayOptions
        .filter((option: Option) => {
            if (option.type) {
                return true
            }
            return projectUserIds.has(option.value)
                || protectedIdSet.has(option.value)
        })
        .map((option: Option) => {
            if (
                !option.type
                && disabledMap.has(option.value)
            ) {
                return {
                    ...option,
                    disabled    : true,
                    hint        : disabledMap.get(option.value),
                }
            }
            return option
        })

    const navigable    = filteredDisplayOptions.filter((o: any) => {
        return !o.type
    })
    const showEmpty    = !!searchString && navigable.length === 0
    const parentObject = {
        state           : {
            currentOptions: props.currentOptions
        },
        onOptionClick   : (option: Option, e: React.MouseEvent) => {
            if ((option as any).disabled) {
                return
            }
            props.onOptionClick(option, e)
            return
        },
        hidePopover     : props.hidePopover,
    }

    const popoverPosition = new PopoverPosition({
        attachment      : "top center",
        targetAttachment: "bottom center",
    })

    return (
        <Popover
            position                = {popoverPosition}
            className               = "min-w-250"
            getTargetFunc           = {() => props.parentRef.current}
            onKeyDown               = {onPopoverKeyDown}
            outsideClickIgnoreClass = "popover"
            onClickOutside          = {props.hidePopover}
        >
            <div className="m-l-10 m-t-10 m-r-10 m-b-10">
                <FieldMultiselect_PopoverSearch
                    className       = "p-0 w-100p"
                    searchable      = {props.field.isSearchable}
                    searchString    = {searchString ?? undefined}
                    onHidePopover   = {props.hidePopover}
                    onSearchChange  = {(s: string) => setSearchString(s)}
                />
            </div>

            <div className={Classes("ui5-popover-menu", props.field.classes.PopoverMenu, {'with-help': isManager && PopoverMenuMore})}>
                <VScroll
                    ref             = {scrollRef}
                    minDragHeight   = {20}
                    containerClass  = {props.field.classes.PopoverMenuScrollContainer}
                    scrollVisibility= "auto"
                >
                    {showEmpty
                        ?
                        <div className="ui5-txt-not-important p-15 txt-center">No users found</div>
                        :
                        <FieldMultiselect_PopoverOptionsSelector
                            ref             = {optionsSelectorRef}
                            parent          = {parentObject}
                            field           = {props.field}
                            model           = {props.model}
                            options         = {filteredDisplayOptions}
                            onUpdateScroll  = {updateScroll}
                        />
                    }
                </VScroll>

                {isManager && PopoverMenuMore &&
                    <PopoverMenuMore
                        parent  = {parentObject}
                        field   = {props.field}
                        model   = {props.model}
                    />
                }

                {task.acl.canAddUser &&
                    <div className  = "option pill"
                         onClick    = {onAddUser}
                    >
                        <div className="ico i5-user-add" />
                        <div className="ui5-txt-less-important">Add user...</div>
                    </div>
                }
            </div>
        </Popover>
    )
}
